/**
 * ProfileRepository - Repositorio unificado para Perfiles
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 *
 * NOTA: Este repositorio es diferente a los demás:
 * - No se sincroniza con Supabase (local-only para sesión)
 * - Solo hay un perfil por dispositivo
 * - No extiende BaseRepository porque no sigue el patrón CRUD estándar
 */

import { IProfileRepository, CurrentUserInfo, ProfileRecord } from '../interfaces/IProfileRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { emitLocalUpdate } from '../syncHelpers';

/**
 * Repositorio unificado de Perfiles
 *
 * Este repositorio funciona tanto en web (con DexieAdapter) como en mobile (con SQLiteAdapter).
 */
export class ProfileRepository implements IProfileRepository {

  constructor(private readonly db: IDatabaseAdapter) {}

  // ==================== Métodos Específicos de Perfil ====================

  async getCurrentUserInfo(): Promise<CurrentUserInfo> {
    try {
      const profiles = await this.getAll();
      if (profiles.length === 0) {
        return { userId: '', empresaId: '' };
      }

      const profile = profiles[0];
      return {
        userId: profile.user_id || '',
        empresaId: profile.empresa_activa_id || ''
      };
    } catch (error) {
      console.error('[ProfileRepository] Error getting current user info:', error);
      return { userId: '', empresaId: '' };
    }
  }

  async getCurrentProfile(): Promise<ProfileRecord | undefined> {
    const profiles = await this.getAll();
    return profiles[0] as ProfileRecord | undefined;
  }

  async setCurrentProfile(userId: string, empresaId: string, role: string | null = null): Promise<void> {
    console.log('[ProfileRepository.setCurrentProfile] Saving profile:', { userId, empresaId, role });

    // Validar y convertir el role al tipo correcto
    const validRole: 'usuario' | 'admin' = role === 'admin' ? 'admin' : 'usuario';

    // Dedup: si el profile actual ya es idéntico (mismo user/empresa/role), no
    // re-escribir ni emitir evento. Esto evita la ráfaga de eventos LOCAL UPDATE
    // de `profiles` que disparaba reconexiones de realtime en cada carga de
    // profile al arrancar (onAuthStateChange dispara INITIAL_SESSION +
    // TOKEN_REFRESHED seguidos).
    const existing = await this.getCurrentProfile();
    if (
      existing &&
      existing.user_id === userId &&
      existing.empresa_activa_id === empresaId &&
      existing.role === validRole
    ) {
      console.log('[ProfileRepository.setCurrentProfile] Profile sin cambios, omitiendo escritura y evento');
      return;
    }

    const now = new Date().toISOString();
    const profile: ProfileRecord = {
      user_id: userId,
      empresa_activa_id: empresaId,
      role: validRole,
      updated_at: now,
      created_at: now
    };

    console.log('[ProfileRepository.setCurrentProfile] Inserting profile:', profile);

    // Para Dexie/IndexedDB: usar bulkPutSilent que maneja better la inserción
    // En lugar de insert() que puede fallar con esquemas sin clave primaria explícita
    await this.clear();
    await this.bulkPutSilent([profile]);

    // Verify it was saved
    const saved = await this.getAll();
    console.log('[ProfileRepository.setCurrentProfile] Verification - profiles count after save:', saved.length);
  }

  // ==================== Methods Required by SyncManager ====================

  async clear(): Promise<void> {
    await this.db.clear('profiles');
  }

  async bulkPut(items: ProfileRecord[]): Promise<void> {
    if (!items || items.length === 0) return;

    await this.db.bulkUpsert('profiles', items);

    // Emitir eventos locales para que los componentes se actualicen
    items.forEach(item => {
      emitLocalUpdate('profiles', item as unknown as Record<string, unknown>);
    });
  }

  async bulkPutSilent(items: ProfileRecord[]): Promise<void> {
    if (!items || items.length === 0) return;

    await this.db.bulkUpsert('profiles', items);

    // Emitir eventos locales para que los componentes se actualicen (no sincroniza con Supabase)
    items.forEach(item => {
      emitLocalUpdate('profiles', item as unknown as Record<string, unknown>);
    });
  }

  async count(): Promise<number> {
    return this.db.count('profiles');
  }

  async bulkDelete(ids: string[]): Promise<void> {
    if (!ids || ids.length === 0) return;
    await this.db.bulkDelete('profiles', ids);
  }

  async getAll(): Promise<ProfileRecord[]> {
    return this.db.getAll<ProfileRecord>('profiles');
  }

  async getLastByDate(dateColumn: string = 'updated_at'): Promise<ProfileRecord | undefined> {
    try {
      const results = await this.db.query<ProfileRecord>('profiles', undefined, {
        column: dateColumn,
        direction: 'desc'
      });
      return results[0] || undefined;
    } catch (error) {
      // Si la columna no existe o hay error, devolver el primer perfil
      const profiles = await this.getAll();
      if (profiles.length === 0) return undefined;

      // Intentar ordenar manualmente por la columna de fecha
      return profiles.reduce((latest, current) => {
        const latestDate = (latest as any)[dateColumn] || '';
        const currentDate = (current as any)[dateColumn] || '';
        return currentDate > latestDate ? current : latest;
      });
    }
  }
}
